<template>
  <section-view class="chart-view" :title="title" :tips="tips" @tip="tip">
    <div ref="chart" :class="['chart', {legend, zoom}]"></div>
    <div class="legends" v-if="legends && legends.length > 0">
      <ul>
        <li v-for="(item, index) in legends" :key="index">
          <span>{{item.name}}</span>
          <i v-for="(color, idx) in item.colors" :key="idx" :style="{background: color}"></i>
        </li>
      </ul>
    </div>
  </section-view>
</template>

<script>
  import SectionView from "@/components/SectionView";
  import {rgba2rgb} from "../utils/Helper";

  /**
   * 瀑布柱状图卡片
   */
  export default {
    name: "WaterfallCard",
    components: {SectionView},

    props: {
      // 提示
      tips: {
        type: Boolean,
        default: false
      },
      // 标题
      title: {
        type: String,
        require: true
      },
      // 内容
      list: {
        type: Array,
        require: true
      },
      // 列名匹配项
      labelKey: {
        type: String,
        require: true
      },
      // 缩写列名匹配项
      simpleLabelKey: {
        type: String,
        require: false
      },
      // 数据匹配项
      valueKey: {
        type: [Object, Array],
        require: true
      },
      // 数据条颜色属性
      colorKey: {
        type: [String, Array],
        require: false
      },
      // 数据项所在轴
      axis: {
        type: String,
        default: 'y'
      },
      // 自定义转换
      format: {
        type: Boolean,
        default: false
      },
      // 值转换
      valueFormatter: {
        type: String,
        require: false
      },
      legend: {
        type: Boolean,
        default: false
      },
      stack: {
        type: String,
        require: false
      },
      // 折线图数据匹配项
      line: {
        type: String,
        require: false
      },
      // 定义 legend 数据
      legends: {
        type: Array,
        require: false
      }
    },

    computed: {
      len() {
        return this.list.length * Object.keys(this.valueKey).length;
      },

      zoom() {
        return this.len > 10 && this.axis === 'y';
      }
    },

    mounted() {
      let chart = this.$echarts.init(this.$refs.chart);
      let labels = [], series = [], simples = {};
      let tooltip = {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      };

      if (this.stack === 'total') {
        let keys = Object.keys(this.valueKey);
        series[0] = {
          name: this.valueKey[keys[0]],
          type: 'bar',
          z: 10,
          data: [],
        };
        series[1] = {
          name: this.valueKey[keys[1]],
          type: 'bar',
          silent: true,
          barGap: '-100%',
          tooltip: {},
          itemStyle: {
            normal: {
              color: '#C3C2C2'
            }
          },
          data: [],
        };

        this.list.forEach((item) => {
          let a = item[keys[0]], b = item[keys[1]];

          series[0].data.push({
            value: a,
            itemStyle: {
              color: item.color,
            }
          });
          series[1].data.push({
            value: b,
          });
          let label = item[this.labelKey];
          labels.push(label);
          if (this.simpleLabelKey) simples[label] = item[this.simpleLabelKey];
        });

        tooltip.formatter = (params) => {
          let item = params[0];
          return '<div style="margin: 0 0 0;line-height:1;">' +
            '<div style="margin: 0 0 0;line-height:1;">' +
            '<div style="font-size:14px;color:#666;font-weight:400;line-height:1;">' + item.name + '</div>' +
              '<div style="margin: 10px 0 0;line-height:1;">' +
                '<div style="margin: 0 0 0;line-height:1;">' +
                  '<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:' + item.color + ';"></span>' +
                  '<span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">实测值</span>' +
                  '<span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + item.value + '</span>' +
                  '<div style="clear:both"></div>' +
                '</div>' +
                '<div style="clear:both"></div>' +
              '</div>' +
            '<div style="clear:both"></div>' +
            '</div>' +
            '<div style="clear:both"></div>' +
            '</div>';
        }
      } else if (this.stack === 'diff') {
        let valueKeys = this.valueKey instanceof Array ? this.valueKey : [this.valueKey];
        let stacks = [];

        for (let i = 0; i < valueKeys.length; i++) {
          let valueKey = valueKeys[i], keys = Object.keys(valueKey);
          let stack = {keys, names: [], values: [], colorKey: this.colorKey instanceof Array ? this.colorKey[i] : (this.colorKey || 'color')};
          for (let key of keys) {
            let data = [], name = valueKey[key];
            series.push({
              name: name,
              type: 'bar',
              data: data,
              stack: this.stack + i
            });
            stack.names.push(name);
            stack.values.push(data);
          }
          stacks.push(stack);
        }

        if (this.line) {
          series[2] = {
            name: '平均值',
            type: 'line',
            itemStyle: {
              color: '#0091FF',
            },
            lineStyle: {
              width: 1.5
            },
            data: []
          };
        }

        const data = (val, start, end, reverse = false) => {
          return {
            value: val,
            itemStyle: {
              color: start ? { x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: reverse ? start : end },
                  { offset: 1, color: reverse ? end : start }
                ]
              } : 'transparent'
            }
          };
        };

        // 用于存放数据
        const map = new Map();
        this.list.forEach((item) => {
          let label = item[this.labelKey];
          // 用于存放 legend 信息
          let legend = [];

          for (let stack of stacks) {
            let keys = stack.keys, names = stack.names, values = stack.values, colorValue = item[stack.colorKey];

            let a = item[keys[0]], b = item[keys[1]], rb = b;
            // 修复数据相等无显示问题
            if (a === b) b = Number(a) + 0.005;
            let min = Math.min(a, b), max = Math.max(a, b);

            // 这里可以调整颜色顺序
            let start = colorValue, end = rgba2rgb(colorValue + '88');
            if ((a < 0 && b > 0) || (b < 0 && a > 0)) {
              // 计算总长度
              let _max = Math.abs(max), _min = Math.abs(min), len = _max + _min;

              // 调整颜色顺序，这里需要同步调整
              let target = a > b ? _min : _max;
              let reverse = a > b;
              // 2022/3/20 更改计算公式
              let mid = rgba2rgb(colorValue + (0x88 + Math.floor(0x77 * (target / len))).toString(16));
              // let mid = rgba2rgb(colorValue + (255 - Math.round(119  * (_max / len))).toString(16));
              values[0].push(data(a, start, mid, reverse));
              values[1].push(data(b, mid, end, reverse));
            } else {
              if (max <= 0) {
                values[0].push(data(max));
                values[1].push(data((min - max), start, end, max === 0 ? a > b : a < b));
              } else {
                values[0].push(data(min));
                values[1].push(data(max - min, start, end, a > b));
              }
            }

            if (this.line) series[2].data.push(item[this.line]);
            legend.push({name: names[0], value: a, color: start});
            legend.push({name: names[1], value: rb, color: end});

            // 当只有 1 个栈时，需要展示变化率
            if (stacks.length === 1) {
              legend.push({name: '变化率', value: Number((rb - a).toFixed(2)), color: 'transparent'});
            }
          }

          map.set(label, legend);
          labels.push(label);
          if (this.simpleLabelKey) simples[label] = item[this.simpleLabelKey];
        });

        tooltip.formatter = (params) => {
          let name = params[0].name;
          let list = map.get(name);
          return '<div style="margin: 0 0 0;line-height:1;">' +
              '<div style="margin: 0 0 0;line-height:1;">' +
                '<div style="font-size:14px;color:#666;font-weight:400;line-height:1;">' + name + '</div>' +
              list.map((item) => {
                  return '<div style="margin: 10px 0 0;line-height:1;">' +
                      '<div style="margin: 0 0 0;line-height:1;">' +
                        '<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:' + item.color + ';"></span>' +
                        '<span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">' + item.name + '</span>' +
                        '<span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + item.value + '</span>' +
                        '<div style="clear:both"></div>' +
                      '</div>' +
                    '<div style="clear:both"></div>' +
                  '</div>'
                }).join('') +
                '<div style="clear:both"></div>' +
              '</div>' +
              '<div style="clear:both"></div>' +
          '</div>';
        }
      } else {
        let keys = Object.keys(this.valueKey);
        this.list.forEach((item) => {
          for (let i = 0; i < keys.length; i++) {
            let cell = series[i], value = item[keys[i]];
            if (this.colorKey) {
              value = {
                value: value,
                itemStyle: {
                  color: item[this.colorKey]
                }
              }
            }

            if (cell == null) {
              cell = series[i] = {
                name: this.valueKey[keys[i]],
                type: 'bar',
                data: [value]
              };
            } else {
              cell.data.push(value)
            }
          }
          let label = item[this.labelKey];
          labels.push(label);
          if (this.simpleLabelKey) simples[label] = item[this.simpleLabelKey];
        });
      }
      let labelAxis = {
        type: 'category',
        data: labels,
        axisLabel: Object.assign({
          interval: this.zoom ? 'auto' : 0,
          textStyle: {
            fontSize:  this.len < 6 || this.auto ? 12 : 11
          }
        }, this.simpleLabelKey ? {
          formatter: function (value) {
            return simples[value];
          }
        } : (this.format ? {
          formatter: function (value) {
            return value.split(' ')[0].split('-').slice(1).join('-');
          }
        } : {}))
      };
      let valueAxis = {
        type: 'value',
        axisLine: {
          show: true
        },
        axisTick: {
          show: true,
          inside: true
        },
        axisLabel: this.valueFormatter ? {
          formatter: this.valueFormatter
        } : {}
      };

      let xAxis,  yAxis, left = 50;
      if (this.axis === 'x') {
        xAxis = valueAxis;
        yAxis = labelAxis;
        left = 70;
      } else {
        yAxis = valueAxis;
        xAxis = labelAxis;
      }

      chart.setOption({
        title: {
          show: false,
          height: 0
        },
        tooltip,
        dataZoom: this.zoom ? [
          {
            type: 'slider'
          },
          {
            type: 'inside'
          }
        ] : null,
        legend: this.legend ? {
          orient: 'vertical',
          right: 10,
          top: 0
        } : {
          show: false,
          height: 0
        },
        grid: {
          top: this.legend ? 60 : 20,
          bottom: this.zoom ? 70 : 20,
          left: left,
          right: 30,
        },
        xAxis: xAxis,
        yAxis: yAxis,
        series: series
      });
    },

    methods: {
      tip() {
        this.$emit('tip');
      }
    }
  }
</script>

<style lang="scss" scoped>
  .chart-view {
    padding: 40px 0;
    ::v-deep header {
      margin: 0 30px;
    }
  }

  .chart {
    height: 412px;

    &.legend {
      height: 512px;
      margin-top: -80px;
    }

    &.zoom {
      height: 522px;
    }
  }

  .legends {
    display: flex;
    margin-top: 20px;
    margin-left: 100px;

    ul {
      display: flex;

      li {
        display: flex;
        align-items: center;
        margin-left: 50px;

        &:first-child {
          margin-left: 0;
        }

        span {
          display: block;
          font-size: 24px;
          color: #333333;
          padding-right: 8px;
        }

        i {
          display: block;
          width: 30px;
          height: 30px;
          border-radius: 10px;
          margin-left: 2px;
        }
      }
    }
  }
</style>